import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { CircleCheckBig, LoaderCircle } from 'lucide-react'
import { apiClient } from '@/lib/api'
import type { PrinterKind, ReceiptDocument } from '@/lib/printing/types'
import { createAdapter } from '@/lib/printing/adapters'
import { availablePrinterOptions } from '@/lib/printing/printerOptions'
import { formatLbp, formatUsd } from '@/lib/money'
import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/primitives'
import { usePrinter } from '@/stores/printer'
import { t } from '@/i18n'

/**
 * Shown the moment a sale completes: what was taken, what change is owed, and every way to put
 * it on paper.
 *
 * Every method is offered on every receipt, not just the one remembered in Settings. A phone
 * moves between whatever printer is actually in reach — a Bluetooth unit today, a share sheet
 * tomorrow because the till's Bluetooth is asleep — and a cashier who picked wrong, or whose
 * first choice failed, needs the rest of the list right there rather than a trip to Settings
 * and back.
 */
export function ReceiptDialog({ saleId, onClose }: { saleId: number | null; onClose: () => void }) {
  const printer = usePrinter()
  const [printError, setPrintError] = useState<string | null>(null)
  /** Which method is currently mid-print, so only that button shows a spinner. */
  const [printingKind, setPrintingKind] = useState<PrinterKind | null>(null)
  const options = useMemo(() => availablePrinterOptions(), [])

  const receipt = useQuery({
    queryKey: ['receipt', saleId],
    queryFn: () => apiClient.get<ReceiptDocument>(`/api/sales/${saleId}/receipt`),
    enabled: saleId !== null,
  })

  useEffect(() => {
    if (saleId !== null) setPrintError(null)
  }, [saleId])

  async function printVia(kind: PrinterKind) {
    if (!receipt.data || printingKind) return
    setPrintingKind(kind)
    setPrintError(null)

    try {
      // Remembered as the default for next time — a convenience, not a restriction: every
      // option stays clickable here regardless of which one was used last.
      printer.choose(kind)
      await createAdapter(kind).print(receipt.data)
      // Counted only after the adapter succeeded, so a failed print is not logged as a reprint.
      await apiClient.post(`/api/sales/${saleId}/receipt/printed`)
    } catch (caught) {
      setPrintError(caught instanceof Error ? caught.message : 'Could not print.')
    } finally {
      setPrintingKind(null)
    }
  }

  const change =
    (receipt.data?.changeLbp ?? 0) > 0
      ? formatLbp(receipt.data!.changeLbp)
      : (receipt.data?.changeUsd ?? 0) > 0
        ? formatUsd(receipt.data!.changeUsd)
        : null

  return (
    <Modal
      open={saleId !== null}
      onClose={onClose}
      title={t('Sale complete')}
      description={receipt.data?.number}
      footer={
        <Button variant="secondary" full onClick={onClose}>
          {t('Next sale')}
        </Button>
      }
    >
      <div className="flex flex-col items-center py-2 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-success-500/15">
          <CircleCheckBig className="h-7 w-7 text-success-500" />
        </div>

        <p className="tabular mt-4 text-3xl font-semibold">
          {receipt.data ? formatUsd(receipt.data.totalUsd) : '—'}
        </p>
        {receipt.data && (
          <p className="tabular text-sm text-[var(--text-secondary)]">
            {formatLbp(receipt.data.totalLbp)}
          </p>
        )}

        {change && (
          <div className="surface-sunken mt-5 w-full rounded-xl p-4">
            <p className="text-sm text-[var(--text-secondary)]">{t('Change to give')}</p>
            <p className="tabular mt-1 text-2xl font-semibold text-success-500">{change}</p>
          </div>
        )}

        <div className="mt-5 w-full text-left">
          <p className="mb-2 text-xs font-medium text-[var(--text-secondary)]">{t('Print via')}</p>
          <div className="grid grid-cols-2 gap-2">
            {options.map((option) => {
              const Icon = option.icon
              const isThisPrinting = printingKind === option.kind
              const isLastUsed = printer.kind === option.kind

              return (
                <button
                  key={option.kind}
                  type="button"
                  onClick={() => printVia(option.kind)}
                  disabled={printingKind !== null || !receipt.data}
                  className={`surface-raised flex flex-col items-center gap-1.5 rounded-[var(--radius-card)] border p-3 text-center disabled:opacity-50 ${
                    isLastUsed ? 'border-[var(--accent)] ring-1 ring-[var(--accent)]' : ''
                  }`}
                >
                  {isThisPrinting ? (
                    <LoaderCircle className="h-5 w-5 animate-spin text-[var(--text-secondary)]" />
                  ) : (
                    <Icon className="h-5 w-5 text-[var(--text-secondary)]" />
                  )}
                  <span className="text-xs font-medium leading-tight">{option.title}</span>
                </button>
              )
            })}
          </div>
        </div>

        {printError && (
          <p role="alert" className="mt-3 w-full rounded-lg bg-danger-500/10 px-3 py-2 text-sm text-danger-500">
            {printError}
          </p>
        )}
      </div>
    </Modal>
  )
}
